import type { ApiResponse } from "../../../types/shared";
import httpClient from "../../../utils/httpClient";
import type {
  Integration,
  IntegrationListItem,
  IntegrationInitParams,
  IntegrationInitResponse,
  IntegrationCompleteOAuth2Params,
  IntegrationCompleteTwitterParams,
} from "../interfaces";

interface IIntegrationService {
  getAllIntegrations(): Promise<IntegrationListItem[]>;

  // Instagram
  initInstagram(
    params: IntegrationInitParams
  ): Promise<IntegrationInitResponse>;
  completeInstagram(
    params: IntegrationCompleteOAuth2Params
  ): Promise<Integration>;

  // Facebook
  initFacebook(params: IntegrationInitParams): Promise<IntegrationInitResponse>;
  completeFacebook(
    params: IntegrationCompleteOAuth2Params
  ): Promise<Integration>;

  // TikTok
  initTiktok(params: IntegrationInitParams): Promise<IntegrationInitResponse>;
  completeTiktok(params: IntegrationCompleteOAuth2Params): Promise<Integration>;

  // YouTube
  initYoutube(params: IntegrationInitParams): Promise<IntegrationInitResponse>;
  completeYoutube(
    params: IntegrationCompleteOAuth2Params
  ): Promise<Integration>;

  // LinkedIn
  initLinkedin(params: IntegrationInitParams): Promise<IntegrationInitResponse>;
  completeLinkedin(
    params: IntegrationCompleteOAuth2Params
  ): Promise<Integration>;

  // Twitter (OAuth 1.0a)
  initTwitter(params: IntegrationInitParams): Promise<IntegrationInitResponse>;
  completeTwitter(
    params: IntegrationCompleteTwitterParams
  ): Promise<Integration>;
}

class IntegrationService implements IIntegrationService {
  private base = "/v1/integration";

  getAllIntegrations = async (): Promise<IntegrationListItem[]> => {
    const res = await httpClient.get<ApiResponse<IntegrationListItem[]>>(
      this.base
    );
    return res.data.data;
  };

  // --- Instagram ---
  initInstagram = async (
    params: IntegrationInitParams
  ): Promise<IntegrationInitResponse> => {
    const res = await httpClient.post<ApiResponse<IntegrationInitResponse>>(
      `${this.base}/instagram/init`,
      { companyId: params.companyId }
    );

    return res.data.data;
  };

  completeInstagram = async (
    params: IntegrationCompleteOAuth2Params
  ): Promise<Integration> => {
    const res = await httpClient.post<ApiResponse<Integration>>(
      `${this.base}/instagram/complete`,
      { code: params.code, companyId: params.companyId }
    );

    return res.data.data;
  };

  // --- Facebook ---
  initFacebook = async (
    params: IntegrationInitParams
  ): Promise<IntegrationInitResponse> => {
    const res = await httpClient.post<ApiResponse<IntegrationInitResponse>>(
      `${this.base}/facebook/init`,
      { companyId: params.companyId }
    );

    return res.data.data;
  };

  completeFacebook = async (
    params: IntegrationCompleteOAuth2Params
  ): Promise<Integration> => {
    const res = await httpClient.post<ApiResponse<Integration>>(
      `${this.base}/facebook/complete`,
      { code: params.code, companyId: params.companyId }
    );

    return res.data.data;
  };

  // --- TikTok ---
  initTiktok = async (
    params: IntegrationInitParams
  ): Promise<IntegrationInitResponse> => {
    const res = await httpClient.post<ApiResponse<IntegrationInitResponse>>(
      `${this.base}/tiktok/init`,
      { companyId: params.companyId }
    );

    return res.data.data;
  };

  completeTiktok = async (
    params: IntegrationCompleteOAuth2Params
  ): Promise<Integration> => {
    const res = await httpClient.post<ApiResponse<Integration>>(
      `${this.base}/tiktok/complete`,
      { code: params.code, companyId: params.companyId }
    );

    return res.data.data;
  };

  // --- YouTube ---
  initYoutube = async (
    params: IntegrationInitParams
  ): Promise<IntegrationInitResponse> => {
    const res = await httpClient.post<ApiResponse<IntegrationInitResponse>>(
      `${this.base}/youtube/init`,
      { companyId: params.companyId }
    );

    return res.data.data;
  };

  completeYoutube = async (
    params: IntegrationCompleteOAuth2Params
  ): Promise<Integration> => {
    const res = await httpClient.post<ApiResponse<Integration>>(
      `${this.base}/youtube/complete`,
      { code: params.code, companyId: params.companyId }
    );

    return res.data.data;
  };

  // --- LinkedIn ---
  initLinkedin = async (
    params: IntegrationInitParams
  ): Promise<IntegrationInitResponse> => {
    const res = await httpClient.post<ApiResponse<IntegrationInitResponse>>(
      `${this.base}/linkedin/init`,
      { companyId: params.companyId }
    );

    return res.data.data;
  };

  completeLinkedin = async (
    params: IntegrationCompleteOAuth2Params
  ): Promise<Integration> => {
    const res = await httpClient.post<ApiResponse<Integration>>(
      `${this.base}/linkedin/complete`,
      { code: params.code, companyId: params.companyId }
    );

    return res.data.data;
  };

  // --- Twitter (OAuth 1.0a) ---
  initTwitter = async (
    params: IntegrationInitParams
  ): Promise<IntegrationInitResponse> => {
    const res = await httpClient.post<ApiResponse<IntegrationInitResponse>>(
      `${this.base}/twitter/init`,
      { companyId: params.companyId }
    );

    return res.data.data;
  };

  completeTwitter = async (
    params: IntegrationCompleteTwitterParams
  ): Promise<Integration> => {
    const res = await httpClient.post<ApiResponse<Integration>>(
      `${this.base}/twitter/complete`,
      {
        oauthToken: params.oauthToken,
        oauthVerifier: params.oauthVerifier,
        companyId: params.companyId,
      }
    );

    return res.data.data;
  };
}

export const integrationService = new IntegrationService();
